import Dialog from '@cobliteam/ui/components/Dialog';
import Tabs, { TabsContent, TabsList, TabsTrigger } from '@cobliteam/ui/components/Tabs';
import { useState } from 'react';
import type { DevToolsPosition } from '../../lib/devToolsPosition';
import FlagsData from '../FlagsData';
import PermissionsData from '../PermissionsData';
import PositionData from '../PositionData';
import PreferencesData from '../PreferencesData/PreferencesData';
import UserData from '../UserData';

const PANELS = [
  { key: 'userData', label: 'Meus dados' },
  { key: 'flags', label: 'Flags' },
  { key: 'permissions', label: 'Permissões' },
  { key: 'preferences', label: 'Preferências' },
  { key: 'position', label: 'Posição' },
] as const;

interface DeveloperToolsDialogProps {
  open: boolean;
  onClose: () => void;
  position: DevToolsPosition;
  onPositionChange: (position: DevToolsPosition) => void;
}

/**
 * Horizontal tabs are what the design system's `Tabs` styles and types natively
 * (it omits Radix's `orientation`), so five panels sit across the top instead of
 * in a rail. Radix supplies the tablist/tab/tabpanel wiring and focus handling.
 */
const DeveloperToolsDialog = ({ open, onClose, position, onPositionChange }: DeveloperToolsDialogProps) => {
  const [selectedPanel, setSelectedPanel] = useState<string>(PANELS[0].key);

  return (
    // `onOpenChange` carries the next open state; ignoring it and always calling
    // `onClose` would slam the dialog shut the moment anything opens it internally.
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <Dialog.Content size='lg'>
        <Dialog.Header>
          <Dialog.Title>Developer Tools</Dialog.Title>
          <Dialog.Description className='sr-only'>Dados de sessão, flags, permissões e preferências do ambiente de desenvolvimento.</Dialog.Description>
          <Dialog.Close />
        </Dialog.Header>

        {/* Dialog.Body ships `overflow-y-auto`. Left on, it becomes a second scroll
            container and carries the tab row off the top with the content. */}
        <Dialog.Body className='overflow-y-hidden'>
          {/* Fixed height, deliberately — not `max-h`. Panel content ranges 266px
              (Posição) to 4136px (Preferências); sizing the dialog to it swung the
              frame 198px and, because the overlay centres its child, slid the tab
              row ~98px out from under the cursor between tabs. Apple's HIG ("avoid
              resizing the window when people switch tabs, because the change can be
              disorienting") and Material both call that out, so the empty space
              under the short panels is the accepted cost of a stable frame.
              Content is top-aligned rather than centred so the space falls below. */}
          <Tabs value={selectedPanel} onValueChange={setSelectedPanel} className='flex h-[min(70vh,32rem)] flex-col gap-4'>
            {/* No `overflow-x-auto` here: setting one axis to `auto` makes the other
                compute to `auto` too, and the `line` variant's `-mb-px` leaves 1px of
                vertical overflow — which rendered a scrollbar sliver over the tabs.
                Five short labels never needed horizontal scrolling anyway; `flex-wrap`
                handles a viewport too narrow to fit them. */}
            <TabsList className='shrink-0 flex-wrap'>
              {PANELS.map(({ key, label }) => (
                <TabsTrigger key={key} value={key} size='sm'>
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* `min-h-0` is what makes this the scroll container: a flex item
                defaults to `min-height: auto`, so without it the panel refuses to
                shrink below its content and the overflow escapes upward. */}
            <div className='min-h-0 min-w-0 flex-1 overflow-y-auto pr-1'>
              <TabsContent value='userData'>
                <UserData />
              </TabsContent>
              <TabsContent value='flags'>
                <FlagsData />
              </TabsContent>
              <TabsContent value='permissions'>
                <PermissionsData />
              </TabsContent>
              <TabsContent value='preferences'>
                <PreferencesData />
              </TabsContent>
              <TabsContent value='position'>
                <PositionData position={position} onPositionChange={onPositionChange} />
              </TabsContent>
            </div>
          </Tabs>
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  );
};

export default DeveloperToolsDialog;
